// ============================================================
// Advanced Binding Resolver
// Resolves advanced field placements against case responses and
// template snapshots. Pure logic — no database calls, no side effects.
//
// Used by: core-domain resolveDocumentPreviewData (frontend preview)
//          worker documentDataTransformer (PDF generation)
// ============================================================

import { formatBooleanDisplay } from '@firesquirrel/document-render';
import type {
  AdvancedFieldPlacement,
  AdvancedFieldFormatting,
  AdvancedFieldResolvedFrom,
  ResolvedAdvancedField,
} from '@firesquirrel/document-render';

// ---------------------------------------------------------------------------
// Field Catalog — built from a TemplateSnapshot for the builder UI
// ---------------------------------------------------------------------------

export interface FieldCatalogEntry {
  fieldId: string;
  formId: string;
  formName: string;
  label: string;
  type: string;
  defaultValue?: any;
  options?: any[];
  bindingKey: string;
}

export interface FieldCatalog {
  entries: FieldCatalogEntry[];
  formCount: number;
  fieldCount: number;
}

export interface TemplateSnapshotInput {
  forms: Array<{
    formId: string;
    formName: string;
    fields: Array<{
      fieldId: string;
      type: string;
      label: string;
      defaultValue?: any;
      options?: any[];
      [k: string]: any;
    }>;
  }>;
}

/**
 * Build a flat, searchable field catalog from a template snapshot.
 * Used by the builder UI to display available fields for binding.
 */
export function buildFieldCatalog(snapshot: TemplateSnapshotInput): FieldCatalog {
  const entries: FieldCatalogEntry[] = [];

  for (const form of snapshot.forms) {
    for (const field of form.fields) {
      entries.push({
        fieldId: field.fieldId,
        formId: form.formId,
        formName: form.formName,
        label: field.label,
        type: field.type,
        defaultValue: field.defaultValue,
        options: field.options,
        bindingKey: `response:${field.fieldId}`,
      });
    }
  }

  return {
    entries,
    formCount: snapshot.forms.length,
    fieldCount: entries.length,
  };
}

// ---------------------------------------------------------------------------
// Value Formatting
// ---------------------------------------------------------------------------

function formatDate(value: any, format?: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);

  // Use UTC getters so calendar-date values render identically regardless of
  // the viewer's timezone. Inspection-report dates represent a calendar day
  // (not an instant), so the UTC-midnight convention carried by ISO inputs
  // like `'2025-03-15T00:00:00Z'` must round-trip to `2025-03-15` in every
  // TZ. See the local-time bug this replaces: in UTC-07:00 it rendered the
  // prior day.
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');

  if (format === 'YYYY-MM-DD') return `${year}-${month}-${day}`;
  if (format === 'DD/MM/YYYY') return `${day}/${month}/${year}`;
  // Default: MM/DD/YYYY
  return `${month}/${day}/${year}`;
}

function formatBoolean(value: any, formatting?: AdvancedFieldFormatting): string {
  const truthy = value === true || value === 'true' || value === 'yes' || value === 'Yes' || value === 1;
  const style = formatting?.booleanStyle || 'yesNo';
  // G-FID-05 / G-FID-04: delegate to the single shared primitive so the preview and the
  // PDF produce identical boolean output (and the checkbox markup is no longer duplicated).
  return formatBooleanDisplay(truthy, style, {
    trueLabel: formatting?.trueLabel,
    falseLabel: formatting?.falseLabel,
  });
}

function formatArray(value: any, joinWith?: string): string {
  if (!Array.isArray(value)) return String(value ?? '');
  return value.map(String).join(joinWith || ', ');
}

/**
 * Format a raw field value based on field type and formatting config.
 */
export function formatFieldValue(
  value: any,
  fieldType: string,
  formatting?: AdvancedFieldFormatting,
): string {
  if (value === undefined || value === null) return '';

  switch (fieldType) {
    case 'date':
    case 'datetime':
      return formatDate(value, formatting?.dateFormat);
    case 'boolean':
    case 'toggle':
    case 'checkbox':
      return formatBoolean(value, formatting);
    case 'multiSelect':
    case 'checkboxGroup':
    case 'array':
      return formatArray(value, formatting?.joinWith);
    case 'number':
    case 'integer':
    case 'decimal':
      if (formatting?.numberFormat === 'currency') {
        return `$${Number(value).toFixed(2)}`;
      }
      if (formatting?.numberFormat === 'percent') {
        return `${Number(value)}%`;
      }
      return String(value);
    default:
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return String(value);
  }
}

// ---------------------------------------------------------------------------
// Single Binding Resolution
// ---------------------------------------------------------------------------

/**
 * Look up a field definition from the snapshot by fieldId.
 */
function findFieldInSnapshot(
  snapshot: TemplateSnapshotInput | undefined,
  fieldId: string,
): { field: { type: string; label: string; defaultValue?: any }; formName: string } | undefined {
  if (!snapshot) return undefined;
  for (const form of snapshot.forms) {
    for (const field of form.fields) {
      if (field.fieldId === fieldId) {
        return { field, formName: form.formName };
      }
    }
  }
  return undefined;
}

/**
 * Resolve a single advanced field placement against case data.
 *
 * Resolution order:
 * 1. case.responses[fieldId].value (or case.responses[fieldId] if primitive)
 * 2. templateSnapshot field defaultValue
 * 3. binding.fallbackText
 * 4. empty string
 */
export function resolveAdvancedBinding(
  binding: AdvancedFieldPlacement,
  responses: Record<string, any> | undefined,
  snapshot: TemplateSnapshotInput | undefined,
): ResolvedAdvancedField {
  // Prefer an explicit fieldId (preserve valid falsy values like 0 / ''-not-applicable),
  // only deriving from bindingKey when fieldId is genuinely absent.
  const fieldId = binding.fieldId !== undefined && binding.fieldId !== null && binding.fieldId !== ''
    ? binding.fieldId
    : (binding.bindingKey ?? '').replace(/^response:/, '');
  let rawValue: any = undefined;
  let resolvedFrom: AdvancedFieldResolvedFrom = 'fallback';
  let fieldType = 'text';

  // Step 1: Try responses
  if (responses) {
    const responseEntry = responses[fieldId];
    if (responseEntry !== undefined && responseEntry !== null) {
      rawValue = typeof responseEntry === 'object' && responseEntry.value !== undefined
        ? responseEntry.value
        : responseEntry;
      resolvedFrom = 'response';
    }
  }

  // Look up field metadata from snapshot
  const snapshotField = findFieldInSnapshot(snapshot, fieldId);
  if (snapshotField) {
    fieldType = snapshotField.field.type;
  }

  // Step 2: Try snapshot default
  if ((rawValue === undefined || rawValue === null) && snapshotField?.field.defaultValue !== undefined) {
    rawValue = snapshotField.field.defaultValue;
    resolvedFrom = 'snapshot-default';
  }

  // Step 3: Fallback only when there is NO recorded response or snapshot default.
  // An empty string that WAS recorded (resolvedFrom === 'response') is a deliberate
  // value and is preserved — it must not be replaced by fallback text.
  if (rawValue === undefined || rawValue === null) {
    return {
      id: binding.id,
      label: binding.label,
      value: binding.fallbackText || '',
      renderMode: binding.renderMode,
      resolvedFrom: 'fallback',
    };
  }

  // When renderMode is 'checkbox', force checkedUnchecked style so ☑/☐ render
  // correctly in both the bottom table and inline body text tokens
  const effectiveFormatting: AdvancedFieldFormatting | undefined =
    binding.renderMode === 'checkbox'
      ? { ...binding.formatting, booleanStyle: 'checkedUnchecked' }
      : binding.formatting;

  // Format the resolved value
  const formattedValue = formatFieldValue(rawValue, fieldType, effectiveFormatting);

  return {
    id: binding.id,
    label: binding.label,
    value: formattedValue,
    renderMode: binding.renderMode,
    resolvedFrom,
  };
}

// ---------------------------------------------------------------------------
// Token Substitution — {{advField:fieldId}} in HTML body text
// ---------------------------------------------------------------------------

/**
 * Replace `{{advField:fieldId}}` tokens in an HTML string with the already-
 * resolved value for that field.
 *
 * This allows advanced-linked field values (e.g. ☑ / ☐) to be embedded
 * directly inside `bodyText` HTML paragraphs rather than only appearing in
 * the auto-appended "Inspection Data" table at the bottom of the PDF.
 *
 * @param html            Raw HTML string (may contain zero or more tokens).
 * @param resolvedFields  Array of resolved advanced fields for the current case.
 * @returns               HTML with every matched token replaced by its value.
 *                        Unmatched tokens are left unchanged.
 */
export function resolveAdvancedTokensInHtml(
  html: string,
  resolvedFields: ResolvedAdvancedField[],
): string {
  if (!html || resolvedFields.length === 0) return html;

  // Build a quick lookup map from fieldId → resolved value
  const valueMap = new Map<string, string>(
    resolvedFields.map((f) => [f.id, f.value]),
  );

  // Replace every {{advField:someId}} occurrence
  return html.replace(/\{\{advField:([^}]+)\}\}/g, (_match, fieldId: string) => {
    const trimmed = fieldId.trim();
    return valueMap.has(trimmed) ? (valueMap.get(trimmed) ?? '') : _match;
  });
}

// ---------------------------------------------------------------------------
// Preview Snapshot Builder — synthesizes TemplateSnapshotInput from FieldCatalog
// ---------------------------------------------------------------------------

/**
 * Build a TemplateSnapshotInput from a FieldCatalog so the document builder
 * can synthesize the `previewDataSource.advanced.templateSnapshot` block from
 * the linked inspection template's REAL field definitions (Option 3).
 *
 * Boolean / checkbox / toggle fields default to `false` so the preview shows
 * an unchecked box (☐) rather than an empty token — a neutral, informative state
 * that makes the template layout visible without implying a real inspection result.
 *
 * @localFirst
 * Source of Truth: local (FieldCatalog built from Dexie template records)
 * Write Flow:      read-only helper — no writes
 * Sync Strategy:   N/A — pure transform
 * Conflict Resolution: N/A
 */
export function buildSnapshotFromCatalog(catalog: FieldCatalog): TemplateSnapshotInput {
  const formMap = new Map<string, { formId: string; formName: string; fields: TemplateSnapshotInput['forms'][0]['fields'] }>();

  for (const entry of catalog.entries) {
    if (!formMap.has(entry.formId)) {
      formMap.set(entry.formId, { formId: entry.formId, formName: entry.formName, fields: [] });
    }
    const isBooleanLike = entry.type === 'boolean' || entry.type === 'checkbox' || entry.type === 'toggle';
    formMap.get(entry.formId)!.fields.push({
      fieldId: entry.fieldId,
      type: entry.type,
      label: entry.label,
      // Default boolean-like fields to false so the preview renders ☐ (unchecked)
      // instead of an empty/unresolved token.
      defaultValue: entry.defaultValue !== undefined
        ? entry.defaultValue
        : isBooleanLike ? false : undefined,
      options: entry.options,
    });
  }

  return { forms: Array.from(formMap.values()) };
}

// ---------------------------------------------------------------------------
// Batch Resolution
// ---------------------------------------------------------------------------

export interface AdvancedBindingDiagnostics {
  totalBindings: number;
  resolved: number;
  fromResponse: number;
  fromSnapshotDefault: number;
  fromFallback: number;
  empty: number;
}

export interface AdvancedBindingResult {
  fields: ResolvedAdvancedField[];
  diagnostics: AdvancedBindingDiagnostics;
}

/**
 * Resolve all advanced field placements for a case.
 * Returns resolved fields (filtered by includeWhenEmpty) and diagnostics.
 */
export function resolveAllAdvancedBindings(
  bindings: AdvancedFieldPlacement[],
  responses: Record<string, any> | undefined,
  snapshot: TemplateSnapshotInput | undefined,
): AdvancedBindingResult {
  const diagnostics: AdvancedBindingDiagnostics = {
    totalBindings: bindings.length,
    resolved: 0,
    fromResponse: 0,
    fromSnapshotDefault: 0,
    fromFallback: 0,
    empty: 0,
  };

  const fields: ResolvedAdvancedField[] = [];

  for (const binding of bindings) {
    const resolved = resolveAdvancedBinding(binding, responses, snapshot);

    // Track diagnostics
    switch (resolved.resolvedFrom) {
      case 'response':
        diagnostics.fromResponse++;
        break;
      case 'snapshot-default':
        diagnostics.fromSnapshotDefault++;
        break;
      case 'fallback':
        diagnostics.fromFallback++;
        break;
    }

    if (resolved.value === '') {
      diagnostics.empty++;
    }

    // Filter out empty fields if binding says to exclude them
    if (resolved.value === '' && binding.includeWhenEmpty === false) {
      continue;
    }

    diagnostics.resolved++;
    fields.push(resolved);
  }

  return { fields, diagnostics };
}
